/**
 * Gotowe scenariusze zamówienia dla generatora AI.
 *
 * Każdy wstawia PEŁNY opis, a nie hasło, bo model pracuje dużo lepiej na
 * konkretnym zamówieniu: samo „post o Hotelspocie" daje ogólniki, a opis
 * z odbiorcą, treścią i wezwaniem daje układ nadający się do użycia.
 *
 * Wszystkie mówią do hotelarza, nie do gościa — reklama sprzedaje Hotelspot
 * obiektom, a nie obiekt gościom.
 */
export interface AiScenario {
  label: string;
  prompt: string;
  /**
   * Fraza do banku zdjęć pasująca do scenariusza. Po angielsku, bo tak opisana
   * jest biblioteka Magnifica. Dzięki niej zakładka z bankiem otwiera się od
   * razu z sensownymi wynikami, zamiast od pustego pola.
   */
  stockTerm: string;
}

export const AI_SCENARIOS: AiScenario[] = [
  {
    label: 'Mniej telefonów na recepcji',
    prompt: 'Post na LinkedIn o tym, że po wdrożeniu Hotelspota recepcja odbiera o połowę mniej telefonów. '
      + 'Mocny nagłówek, dwa zdania opisu i przycisk „Umów prezentację".',
    stockTerm: 'hotel reception desk staff working',
  },
  {
    label: 'Integracja z PMS',
    prompt: 'Ogłoszenie o nowej integracji Hotelspota z systemem PMS. Plakietka „Nowość", krótki komunikat '
      + 'i jedno zdanie o tym, że dane pobytu nie wymagają przepisywania między systemami.',
    stockTerm: 'hotel front desk computer software',
  },
  {
    label: 'Wynik wdrożenia',
    prompt: 'Kreacja z jedną dużą liczbą: +31% zamówień room service w pierwszym kwartale po wdrożeniu. '
      + 'Pod liczbą krótkie wyjaśnienie i nazwa Hotelspot.',
    stockTerm: 'hotel manager tablet analytics',
  },
  {
    label: 'Webinar dla hotelarzy',
    prompt: 'Zaproszenie na webinar dla dyrektorów hoteli o zwiększaniu przychodu z pokoju bez dodatkowego etatu. '
      + 'Termin, temat i informacja o bezpłatnym udziale.',
    stockTerm: 'online meeting laptop presentation',
  },
  {
    label: 'Opinia dyrektora',
    prompt: 'Kreacja z cytatem dyrektora hotelu o tym, że zamówienia z pokoi przestały się gubić. '
      + 'Pod cytatem podpis z rolą i standardem obiektu.',
    stockTerm: 'hotel manager portrait smiling',
  },
  {
    label: 'Oferta wdrożenia',
    prompt: 'Post o wdrożeniu Hotelspota bez opłaty startowej, z ceną miesięczną i umową na miesiąc. '
      + 'Wyraźna cena i przycisk „Porozmawiajmy".',
    stockTerm: 'modern hotel lobby interior',
  },
  {
    label: 'Zamówienia po godzinach',
    prompt: 'Post o tym, że gość zamawia z pokoju także wtedy, gdy recepcja ma mniej rąk do pracy: '
      + 'zgłoszenie trafia do właściwego zespołu, a nic nie ginie po drodze. Nagłówek i dwa zdania.',
    stockTerm: 'hotel room service tray night',
  },
  {
    label: 'Spa i zabiegi',
    prompt: 'Kreacja o rezerwacji zabiegów spa prosto z pokoju, bez schodzenia do recepcji i bez telefonu. '
      + 'Spokojny ton, jedno zdanie o wyższej sprzedaży zabiegów.',
    stockTerm: 'hotel spa wellness treatment',
  },
  {
    label: 'Śniadania do pokoju',
    prompt: 'Post o zamawianiu śniadania do pokoju z wyborem godziny dostarczenia. '
      + 'Podkreśl, że kuchnia dostaje zamówienia z wyprzedzeniem i lepiej planuje pracę.',
    stockTerm: 'breakfast tray hotel room',
  },
  {
    label: 'Płatność online',
    prompt: 'Kreacja o płatności kartą przy zamówieniu: gość płaci od razu, a rozliczenie trafia na konto obiektu. '
      + 'Wyraźny nagłówek i wzmianka o braku dopisywania do rachunku pokoju.',
    stockTerm: 'contactless payment card smartphone',
  },
  {
    label: 'Gość obcojęzyczny',
    prompt: 'Post o obsłudze gościa obcojęzycznego: hub gościa mówi w jego języku, więc recepcja nie musi tłumaczyć. '
      + 'Nagłówek, jedno zdanie i lista dwóch korzyści.',
    stockTerm: 'international travellers hotel lobby',
  },
  {
    label: 'Bez pobierania aplikacji',
    prompt: 'Kreacja o tym, że gość nie musi niczego instalować: skanuje kod QR i od razu zamawia w przeglądarce. '
      + 'Miejsce na kod QR i krótkie wezwanie do zeskanowania.',
    stockTerm: 'scanning qr code smartphone',
  },
  {
    label: 'Wdrożenie w 48 godzin',
    prompt: 'Post o tym, że uruchomienie Hotelspota w obiekcie zajmuje 48 godzin. '
      + 'Trzy kroki wdrożenia jako numerowana lista i przycisk „Umów rozmowę".',
    stockTerm: 'hotel staff training tablet',
  },
  {
    label: 'Dodatkowa sprzedaż z pokoju',
    prompt: 'Kreacja o dosprzedaży: późne wymeldowanie, dodatkowa noc i usługi dodatkowe kupowane z poziomu pokoju. '
      + 'Nagłówek o przychodzie, którego wcześniej nie było, i krótkie wyliczenie.',
    stockTerm: 'luxury hotel suite room',
  },
  {
    label: 'Panel i statystyki',
    prompt: 'Post o panelu dla obiektu: zamówienia, zgłoszenia i statystyki w jednym miejscu. '
      + 'Miejsce na zrzut ekranu panelu i dwa zdania o podejmowaniu decyzji na danych.',
    stockTerm: 'analytics dashboard laptop screen',
  },
  {
    label: 'Zaproszenie na targi',
    prompt: 'Kreacja z zaproszeniem na stoisko Hotelspota podczas targów hotelarskich. '
      + 'Nazwa wydarzenia, numer stoiska i wezwanie do umówienia spotkania.',
    stockTerm: 'business conference trade fair booth',
  },
  {
    label: 'Zgłoszenia do housekeepingu',
    prompt: 'Post o tym, że gość zgłasza usterkę albo prosi o ręczniki z poziomu pokoju, a zgłoszenie trafia '
      + 'od razu do housekeepingu. Nagłówek o krótszym czasie reakcji i dwa zdania opisu.',
    stockTerm: 'housekeeping hotel staff cleaning room',
  },
  {
    label: 'Późne wymeldowanie',
    prompt: 'Kreacja o sprzedaży późnego wymeldowania z poziomu pokoju: gość klika, obiekt zarabia, '
      + 'recepcja nie musi nic ustalać telefonicznie. Wyraźny nagłówek i krótka notka.',
    stockTerm: 'hotel checkout reception clock',
  },
  {
    label: 'Wypożyczalnia sprzętu',
    prompt: 'Post o wypożyczalni w obiekcie: rowery, sprzęt narciarski i akcesoria rezerwowane z pokoju. '
      + 'Nagłówek o dodatkowym przychodzie i jedno zdanie o braku papierowych list.',
    stockTerm: 'bike rental hotel guest',
  },
  {
    label: 'Atrakcje w okolicy',
    prompt: 'Kreacja o tym, że gość znajduje atrakcje w okolicy w hubie obiektu, bez pytania recepcji. '
      + 'Nagłówek i dwa zdania o dłuższym pobycie i lepszej ocenie obiektu.',
    stockTerm: 'tourist attractions city guide map',
  },
  {
    label: 'Sale konferencyjne',
    prompt: 'Post dla obiektów konferencyjnych: uczestnicy zamawiają przerwy kawowe i zgłaszają potrzeby '
      + 'z poziomu telefonu, bez szukania obsługi. Nagłówek i krótkie wyliczenie korzyści.',
    stockTerm: 'hotel conference room business meeting',
  },
  {
    label: 'Rezerwacja stolika',
    prompt: 'Kreacja o rezerwacji stolika w hotelowej restauracji prosto z pokoju. '
      + 'Podkreśl przewidywalne obłożenie sali i mniej pustych miejsc.',
    stockTerm: 'hotel restaurant table dinner setting',
  },
  {
    label: 'Sieć obiektów',
    prompt: 'Post dla sieci hotelowych: jeden panel dla wszystkich obiektów, wspólne standardy i porównywalne dane. '
      + 'Nagłówek o skali i dwa zdania o zarządzaniu z jednego miejsca.',
    stockTerm: 'hotel chain buildings exterior modern',
  },
  {
    label: 'Opiekun wdrożenia',
    prompt: 'Kreacja o tym, że każdy obiekt dostaje opiekuna wdrożenia i wsparcie po uruchomieniu. '
      + 'Ton spokojny, nagłówek o tym, że nikt nie zostaje sam z systemem.',
    stockTerm: 'customer support headset office team',
  },
];
